// src/contexts/FormAnswersContext.jsx
// Oct 13 — Enterprise-grade Answers store with storage-backed hydration
// Canonical answers keyed by UFI, raw-first storage (no encode here).
// Dropdowns stored as optionIds; labels/transformations are computed on read.
// Compatible with your submission pipeline and table schema.

import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';

import { useFormContext } from './FormContext';
import { useFormLayout } from './FormLayoutContext';
import { useFieldMetadata } from './FieldMetadataContext';
import { useMultiAnswerField } from './MultiAnswerFieldContext';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../utils/storageUtils';
import { applyTransformations } from '../utils/transformationUtils';

// -------------------------------------------
// Context plumbing
// -------------------------------------------
// -------------------------------------------
// Context plumbing (safe defaults so hooks can be called unconditionally)
// -------------------------------------------
const noop = () => {};
const defaultCtx = {
  // state
  answersByUfi: {},

  // getters
  getRawAnswer: () => undefined,
  getDisplayAnswer: () => undefined,
  getAnswersSnapshot: () => null,

  // other text channel (safe defaults)
  getOtherText: () => '',
  setOtherText: noop,
  clearOtherText: noop,

  // setters
  setAnswer: noop,
  clearAnswer: noop,
  resetAll: noop,

  // builders
  buildSubmissionArray: () => [],
  buildDisplaySubmissionArray: () => [],
};


const FormAnswersContext = createContext(defaultCtx);
export const useFormAnswers = () => useContext(FormAnswersContext);


// -------------------------------------------
// Small utils
// -------------------------------------------

/** Returns a plain object shallow-equal check (fast path) */
function shallowEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  for (const k of ka) if (a[k] !== b[k]) return false;
  return true;
}

/** Normalize boolean-like values; keep primitives as-is */
function normalizePrimitive(v) {
  if (v === true || v === false) return v;
  return v ?? '';
}

/** Enforce array for multi-select; remove dupes; coerce falsy to [] */
function normalizeArray(v) {
  if (!Array.isArray(v)) return [];
  // remove duplicates but keep order
  const seen = new Set();
  const out = [];
  for (const x of v) {
    if (!seen.has(x)) {
      seen.add(x);
      out.push(x);
    }
  }
  return out;
}

/** Identifies a dropdown type */
function isDropdownType(t) {
  return t === 'single-select' || t === 'multi-select';
}

/** Value coercion by field type (store *raw*, no encode here) */
function coerceValueForType(fieldType, rawValue) {
  if (fieldType === 'multi-select') return normalizeArray(rawValue);
  if (fieldType === 'single-select') return normalizePrimitive(rawValue);
  if (fieldType === 'date-picker') return normalizePrimitive(rawValue);

  // ─────────────────────────────────────────────────────────────
  // ENTERPRISE: attach values must be JSON-safe in answersByUfi.
  // Never store a live File/Blob reference in answersByUfi.
  //
  // Accepted inputs:
  //  - { kind:'file', tempId, name, type, size, file: File }  -> store token; file goes to runtime cache via setAnswer()
  //  - { kind:'file', tempId, name, type, size }              -> already token
  //  - { assetId, versionNumber? }                            -> stable asset ref
  //  - null/undefined                                         -> null
  // ─────────────────────────────────────────────────────────────
  if (fieldType === 'attach') {
    if (rawValue == null) return null;

    // arrays are handled elsewhere in your attach component; keep as-is if you truly support multi
    if (Array.isArray(rawValue)) return rawValue;

    if (typeof rawValue === 'object') {
      // If caller passed a token already
      if (rawValue.kind === 'file' && rawValue.tempId) {
        const { kind, tempId, name, type, size } = rawValue;
        return { kind, tempId, name, type, size };
      }

      // If caller passed an asset ref
      if (typeof rawValue.assetId === 'string') {
        return {
          assetId: rawValue.assetId,
          ...(typeof rawValue.versionNumber === 'number' ? { versionNumber: rawValue.versionNumber } : {}),
        };
      }

      // If caller passed { file: File } shape (or kind:file with file)
      if (rawValue.file) {
        const f = rawValue.file;
        const tempId = rawValue.tempId || `tmp_${Date.now()}_${Math.random().toString(16).slice(2)}`;
        return {
          kind: 'file',
          tempId,
          name: rawValue.name || f?.name || '',
          type: rawValue.type || f?.type || '',
          size: rawValue.size || f?.size || 0,
        };
      }

      // Unknown object: keep null to avoid poisoning answers with unstable shapes
      return null;
    }

    return null;
  }

  // text-like
  return typeof rawValue === 'string' ? rawValue : String(rawValue ?? '');
}

/** Compute transformed display (caps/spacing), *not* encode */
function transformDisplay(value, meta) {
  // multi-select: transform each label elsewhere; for text fields, transform here
  if (Array.isArray(value)) {
    return value.map(v =>
      applyTransformations(String(v ?? ''), meta?.transformations || {})
    );
  }

  // Date fields:
  // DateFieldComponent already formats + capitalizes the string based on
  // dateFormat + transformations.spacing + transformations.capitalize.
  // Here we just return the canonical string as-is so we don't double-transform.
  if (meta?.type === 'date-picker') {
    return String(value ?? '');
  }

  // text / url / other non-dropdown fields
  return applyTransformations(String(value ?? ''), meta?.transformations || {});
}


// -------------------------------------------
// Provider
// -------------------------------------------
// persist = false → no hydrate/saving (fresh on every load)
// persist = true  → hydrate from storage and save to storage
export const FormAnswersProvider = ({ children, persist = false }) => {
  const { formId } = useFormContext();

  const { layoutConfig } = useFormLayout();
  const { fieldsMetadata } = useFieldMetadata();
  const { dropdownOptions } = useMultiAnswerField();

  // Canonical answer store is by UFI
  // answers shape: { [ufi]: rawValue } where rawValue is:
  //   - string for text/single-select/date
  //   - array<string> for multi-select (optionIds)
  //   - object|null for attach
  const [answersByUfi, setAnswersByUfi] = useState({});

  // keep a live ref so debounced saves always write the newest map
  const answersRef = useRef(answersByUfi);
  useEffect(() => { answersRef.current = answersByUfi; }, [answersByUfi]);

  // ─────────────────────────────────────────────────────────────
  // AUF / attach runtime cache (ephemeral; never persisted)
  // Stores real File objects keyed by tempId so answers stay JSON-safe
  // and stable across rerenders.
  // ─────────────────────────────────────────────────────────────
  const attachRuntimeRef = useRef(new Map()); // tempId -> File

  const registerAttachFile = useCallback((tempId, file) => {
    if (!tempId || !file) return;
    try { attachRuntimeRef.current.set(tempId, file); } catch (_) {}
  }, []);

  const getAttachFile = useCallback((tempId) => {
    if (!tempId) return null;
    try { return attachRuntimeRef.current.get(tempId) || null; } catch (_) { return null; }
  }, []);

  const clearAttachFile = useCallback((tempId) => {
    if (!tempId) return;
    try { attachRuntimeRef.current.delete(tempId); } catch (_) {}
  }, []);

  const clearAllAttachFiles = useCallback(() => {
    try { attachRuntimeRef.current.clear(); } catch (_) {}
  }, []);

  // Debounce-like guard to reduce redundant writes
  const saveTimerAnswers = useRef(null);
  const saveTimerOther   = useRef(null);

  // Keys for storage
  const storageKey = formId ? `form_${formId}_answers` : null;
  const storageLastKey = formId ? `form_${formId}_answers_lastUpdated` : null;

  // ─────────────────────────────────────────────────────────────
  // Other text side-channel (does NOT change dropdown answer type)
  // - dropdown selection stays optionId "__other__" in answersByUfi
  // - typed "Other" text is stored separately here
  // ─────────────────────────────────────────────────────────────
  const [otherTextByUfi, setOtherTextByUfi] = useState({});
  const otherTextRef = useRef(otherTextByUfi);
  useEffect(() => { otherTextRef.current = otherTextByUfi; }, [otherTextByUfi]);

  const otherStorageKey = formId ? `form_${formId}_otherTextByUfi` : null;
  const otherStorageLastKey = formId ? `form_${formId}_otherTextByUfi_lastUpdated` : null;

  // -------------------------------------------
  // Safety: when persist=false, never carry answers across form switches
  // -------------------------------------------
useEffect(() => {
  // When persistence is disabled, this provider behaves like an in-memory cache.
  // If the user navigates to a different formId while the provider stays mounted,
  // we must clear answers so prior-form answers don't leak into the next form.
  if (!persist) {
    setAnswersByUfi({});
    // keep ref in sync so any snapshot-based reads don't see stale values
    answersRef.current = {};
//    console.log('[FormAnswersContext] persist=false → cleared answers on formId change:', formId);

    // ─────────────────────────────────────────────────────────────
    // also clear otherTextByUfi for this form switch
    // ─────────────────────────────────────────────────────────────
    setOtherTextByUfi({});
    otherTextRef.current = {};
    clearAllAttachFiles();
  }
}, [formId, persist, clearAllAttachFiles]);


// -------------------------------------------
// Hydration: load once per formId
// -------------------------------------------
useEffect(() => {
  // Skip hydration when persistence is off
  if (!persist || !formId || !storageKey) {
    if (!persist) {
      // Helpful trace while testing
//      console.log('[FormAnswersContext] Persistence disabled (persist=false) → skipping hydration');
    }
    return;
  }

  const stored = safeGetItem(storageKey);
  if (stored && typeof stored === 'object') {

    // ─────────────────────────────────────────────────────────────
    // AUF Flicker Diagnostics (LOG ONLY)
    // Enable in DevTools:
    //   window.__DEBUG_AUF_FLICKER__ = true
    // ─────────────────────────────────────────────────────────────
    try {
      if (typeof window !== 'undefined' && window.__DEBUG_AUF_FLICKER__ === true) {
        // Try to find the attach UFI (if metadata is available by now)
        const attachMeta = Object.values(fieldsMetadata || {}).find(m => m?.type === 'attach');
        const attachUfi = attachMeta?.ufi || null;

        const v = attachUfi ? stored?.[attachUfi] : undefined;
        const isObj = v && typeof v === 'object' && !Array.isArray(v);
        const hasFile = !!(isObj && v.file);

        console.log('[AUF:Flicker][FormAnswersContext] hydration read from storage', {
          formId,
          storageKey,
          persist,
          hasStoredObject: true,
          storedKeysCount: Object.keys(stored || {}).length,
          attachUfi,
          attachStoredType: v == null ? 'null' : Array.isArray(v) ? 'array' : typeof v,
          attachStoredKeys: isObj ? Object.keys(v) : [],
          attachHasFile: hasFile,
          attachFileName: hasFile ? (v.file?.name || null) : (v?.name || v?.filename || null),
          attachAssetId: isObj ? (v.assetId || v.id || null) : null,
          attachVersionNumber: (isObj && typeof v.versionNumber === 'number') ? v.versionNumber : null,
        });
      }
    } catch (e) {
      // non-fatal
    }

    setAnswersByUfi(prev => {

      // ─────────────────────────────────────────────────────────────
      // AUF Flicker Diagnostics: did hydration actually change state?
      // ─────────────────────────────────────────────────────────────
      try {
        if (typeof window !== 'undefined' && window.__DEBUG_AUF_FLICKER__ === true) {
          const eq = shallowEqual(prev, stored);
          console.log('[AUF:Flicker][FormAnswersContext] hydration apply decision', {
            formId,
            storageKey,
            willUpdateState: !eq,
            prevKeysCount: Object.keys(prev || {}).length,
            storedKeysCount: Object.keys(stored || {}).length,
          });
        }
      } catch (e) {
        // non-fatal
      }

      return shallowEqual(prev, stored) ? prev : stored;
    });
//    console.log('[FormAnswersContext] Hydrated answers from storage:', stored);
  } else {

    // ─────────────────────────────────────────────────────────────
    // AUF Flicker Diagnostics: storage had no answers; we clear
    // ─────────────────────────────────────────────────────────────
    try {
      if (typeof window !== 'undefined' && window.__DEBUG_AUF_FLICKER__ === true) {
        console.log('[AUF:Flicker][FormAnswersContext] hydration found no stored answers → clearing', {
          formId,
          storageKey,
          persist,
          storedType: stored == null ? 'null' : typeof stored,
        });
      }
    } catch (e) {
      // non-fatal
    }

//    console.log('[FormAnswersContext] No stored answers found for form', formId);
    setAnswersByUfi({});
  }
}, [persist, formId, storageKey, fieldsMetadata]);

// ─────────────────────────────────────────────────────────────
// Hydration for otherTextByUfi
// ─────────────────────────────────────────────────────────────
useEffect(() => {
  if (!persist || !formId || !otherStorageKey) return;

  const storedOther = safeGetItem(otherStorageKey);
  if (storedOther && typeof storedOther === 'object') {
    setOtherTextByUfi(prev => (shallowEqual(prev, storedOther) ? prev : storedOther));
//    console.log('[FormAnswersContext] Hydrated otherTextByUfi from storage:', storedOther);
  } else {
    setOtherTextByUfi({});
  }
}, [persist, formId, otherStorageKey]);




  // -------------------------------------------
  // Helper: resolve UFI for a given fieldId|ufi
  // -------------------------------------------
  const resolveUfi = useCallback((idOrUfi) => {
    if (!idOrUfi) return null;
    // If caller passed UFI, common case: just return it when metadata has same ufi
    const asUfiMeta = Object.values(fieldsMetadata || {}).find(m => m?.ufi === idOrUfi);
    if (asUfiMeta) return idOrUfi;

    // Otherwise, it's likely a fieldId → map to UFI
    const meta = fieldsMetadata?.[idOrUfi];
    return meta?.ufi || null;
  }, [fieldsMetadata]);

  // -------------------------------------------
  // Public getters
  // -------------------------------------------

  /**
   * getRawAnswer: returns raw stored value by fieldId or UFI.
   * Raw means:
   *  - text: string
   *  - single-select: optionId string
   *  - multi-select: optionId array
   *  - date: string
   *  - attach: object|null
   */
  const getRawAnswer = useCallback((fieldIdOrUfi) => {
    const ufi = resolveUfi(fieldIdOrUfi);
    if (!ufi) return undefined;
    return answersByUfi[ufi];
  }, [answersByUfi, resolveUfi]);

  const getAnswer = getRawAnswer; // alias for clarity/compatibility

  /**
   * getDisplayAnswer: computes a *transformed* value to display
   * for text-like fields (capitalize/spacing), and for dropdowns,
   * maps optionId(s) → labels (and applies transformations to labels).
   * No URL encode here—`FormActionsContext` handles encode during submit.
   */
  const getDisplayAnswer = useCallback((fieldIdOrUfi) => {
    const ufi = resolveUfi(fieldIdOrUfi);
    if (!ufi) return undefined;

    // find the field meta by ufi
    const meta = Object.values(fieldsMetadata || {}).find(m => m?.ufi === ufi);
    if (!meta) return answersByUfi[ufi];

    const type = meta.type;
    const raw = answersByUfi[ufi];

    if (isDropdownType(type)) {
      const setId = meta.optionsSetUFI;
      const opts = setId ? dropdownOptions?.[setId] || [] : [];

      if (type === 'single-select') {
        const match = opts.find(o => o.optionId === raw);
        const label = match ? match.label : raw;
        return transformDisplay(label, meta);
      }
      if (type === 'multi-select') {
        const rawArr = Array.isArray(raw) ? raw : [];
        const mapped = rawArr.map(id => {
          const m = opts.find(o => o.optionId === id);
          return m ? m.label : id;
        });
        return mapped.map(l => transformDisplay(l, meta));
      }
    }

    // text-like / date / attach (no encode here)
    return transformDisplay(raw, meta);
  }, [answersByUfi, fieldsMetadata, dropdownOptions, resolveUfi]);

 /**
  * getAnswersSnapshot: returns the latest answers map via ref.
  * Useful as a lightweight “change token” so renderers don’t depend on the whole map.
  */
 const getAnswersSnapshot = useCallback(() => answersRef.current, []);

  // ─────────────────────────────────────────────────────────────
  // Other text getters/setters (UFI-keyed)
  // ─────────────────────────────────────────────────────────────

  const getOtherText = useCallback((fieldIdOrUfi) => {
    const ufi = resolveUfi(fieldIdOrUfi);
    if (!ufi) return '';
    return otherTextByUfi?.[ufi] || '';
  }, [otherTextByUfi, resolveUfi]);

  const setOtherText = useCallback((fieldIdOrUfi, text) => {
    const ufi = resolveUfi(fieldIdOrUfi);
    if (!ufi) {
      console.warn('[FormAnswersContext] setOtherText called with unknown field:', fieldIdOrUfi);
      return;
    }

    const nextText = String(text ?? '');

    setOtherTextByUfi(prev => {
      const next = { ...prev, [ufi]: nextText };
      return shallowEqual(prev, next) ? prev : next;
    });

    // Persist (debounced) similar to setAnswer, but separate key space
    if (persist && otherStorageKey) {
        if (saveTimerOther.current) clearTimeout(saveTimerOther.current);
        saveTimerOther.current = setTimeout(() => {
        try {
          const latestFromRef = otherTextRef.current && typeof otherTextRef.current === 'object'
            ? { ...otherTextRef.current, [ufi]: nextText }
            : { [ufi]: nextText };

          safeSetItem(otherStorageKey, latestFromRef);
          safeSetItem(otherStorageLastKey, new Date().toISOString());
//          console.log('[FormAnswersContext] Persisted otherTextByUfi → storage for form', formId);
        } catch (e) {
          console.error('[FormAnswersContext] Failed to persist otherTextByUfi:', e);
        }
      }, 150);
    }
  }, [resolveUfi, persist, otherStorageKey, otherStorageLastKey, formId]);

  const clearOtherText = useCallback((fieldIdOrUfi) => {
    const ufi = resolveUfi(fieldIdOrUfi);
    if (!ufi) return;

    setOtherTextByUfi(prev => {
      if (!(ufi in prev)) return prev;
      const next = { ...prev };
      delete next[ufi];
      if (otherStorageKey) safeSetItem(otherStorageKey, next);
      return next;
    });
  }, [resolveUfi, otherStorageKey]);

  // -------------------------------------------
  // Public setters
  // -------------------------------------------

  /**
   * setAnswer: accepts fieldId or UFI and a raw value.
   * Coerces the value by field type (e.g., multi-select arrays)
   * and persists (debounced) to storage.
   */
  const setAnswer = useCallback((fieldIdOrUfi, rawValue) => {
    const ufi = resolveUfi(fieldIdOrUfi);
    if (!ufi) {
      console.warn('[FormAnswersContext] setAnswer called with unknown field:', fieldIdOrUfi);
      return;
    }

const meta = Object.values(fieldsMetadata || {}).find(m => m?.ufi === ufi);
const fieldType = meta?.type || 'text';
const coerced = coerceValueForType(fieldType, rawValue);

// ─────────────────────────────────────────────────────────────
// ENTERPRISE: AUF attach runtime cache registration (LOG ONLY)
// - If caller provides a real File (rawValue.file), store it in runtime cache
//   keyed by tempId so answersByUfi stays JSON-safe + stable.
// - Requires Step 1A: registerAttachFile(tempId, file) to exist in this file.
// ─────────────────────────────────────────────────────────────
try {
  if (fieldType === 'attach' && rawValue && typeof rawValue === 'object') {
    const maybeFile = rawValue.file;
    const maybeTempId = rawValue.tempId || coerced?.tempId || null;

    if (maybeFile && maybeTempId) {
      // Store the real File in runtime cache (never persisted)
      if (typeof registerAttachFile === 'function') {
        registerAttachFile(maybeTempId, maybeFile);
      }

      // Optional trace for flicker debugging
      const __DBG__ =
        typeof window !== 'undefined' && window.__DEBUG_AUF_FLICKER__ === true;

      if (__DBG__) {
        console.log('[AUF:Flicker][FormAnswersContext] registerAttachFile', {
          formId,
          ufi,
          tempId: maybeTempId,
          fileName: maybeFile?.name || null,
          fileType: maybeFile?.type || null,
          fileSize: maybeFile?.size || null,
        });
      }
    }
  }
} catch (e) {
  // non-fatal
}

// ─────────────────────────────────────────────────────────────
// AUF Flicker Diagnostics (LOG ONLY)
// Enable in DevTools:
//   window.__DEBUG_AUF_FLICKER__ = true
// ─────────────────────────────────────────────────────────────
const __DBG__ =
  typeof window !== 'undefined' && window.__DEBUG_AUF_FLICKER__ === true;

if (__DBG__ && fieldType === 'attach') {
  try {
    const prevVal = answersRef.current?.[ufi];
    const prevSameRef = prevVal === coerced;

    // best-effort structural fingerprint (avoid logging the actual File blob)
    const fp = (v) => {
      if (!v || typeof v !== 'object') return { kind: v == null ? 'null' : typeof v };
      const hasFile = !!v.file;
      const file = v.file;
      return {
        kind: v.kind || '(none)',
        keys: Object.keys(v),
        hasFile,
        fileName: hasFile ? (file?.name || null) : (v.name || v.filename || null),
        fileType: hasFile ? (file?.type || null) : (v.type || null),
        fileSize: hasFile ? (file?.size || null) : (v.size || null),
        assetId: v.assetId || v.id || null,
        versionNumber: (typeof v.versionNumber === 'number') ? v.versionNumber : null,
        // common transient keys that may cause churn
        previewUrl: v.previewUrl || v.previewURL || null,
      };
    };

    console.log('[AUF:Flicker][FormAnswersContext] setAnswer(attach)', {
      formId,
      ufi,
      prevSameRef,
      rawType: rawValue == null ? 'null' : Array.isArray(rawValue) ? 'array' : typeof rawValue,
      coercedType: coerced == null ? 'null' : Array.isArray(coerced) ? 'array' : typeof coerced,
      prev: fp(prevVal),
      next: fp(coerced),
      stack: new Error().stack, // tells us WHO is calling setAnswer
    });
  } catch (e) {
    // non-fatal
  }
}

setAnswersByUfi(prev => {
  const next = { ...prev, [ufi]: coerced };
  return shallowEqual(prev, next) ? prev : next;
});

    // Debounce save to storage to avoid write storms
if (persist && storageKey) {
  if (saveTimerAnswers.current) clearTimeout(saveTimerAnswers.current);
  saveTimerAnswers.current = setTimeout(() => {
    try {
      // Use a ref snapshot to avoid stale closure; fallback to latest state
 const latestFromRef = answersRef.current && typeof answersRef.current === 'object'
   ? { ...answersRef.current, [ufi]: coerced }
   : { [ufi]: coerced };

      safeSetItem(storageKey, latestFromRef);
      safeSetItem(storageLastKey, new Date().toISOString());
//      console.log('[FormAnswersContext] Persisted answers → storage for form', formId);
    } catch (e) {
      console.error('[FormAnswersContext] Failed to persist answers:', e);
    }
  }, 150);
} else if (!persist) {
  // Helpful trace while testing
//   console.log('[FormAnswersContext] Persistence disabled (persist=false) → not saving to storage');
}

}, [fieldsMetadata, resolveUfi, storageKey, storageLastKey, formId, persist, registerAttachFile]);

/**
 * clearAnswer: remove a single UFI from store (and storage).
 */
const clearAnswer = useCallback((fieldIdOrUfi) => {
  const ufi = resolveUfi(fieldIdOrUfi);
  if (!ufi) return;

  // ─────────────────────────────────────────────────────────────
  // ENTERPRISE: if this answer is an attach token, clear its runtime file
  // without nuking the entire runtime cache.
  // ─────────────────────────────────────────────────────────────
  try {
    const meta = Object.values(fieldsMetadata || {}).find(m => m?.ufi === ufi);
    if (meta?.type === 'attach') {
      const prevVal = answersRef.current?.[ufi];
      const tempId = (prevVal && typeof prevVal === 'object' && prevVal.kind === 'file')
        ? prevVal.tempId
        : null;

      if (tempId) {
        clearAttachFile(tempId);
      }
    }
  } catch (_) {
    // non-fatal
  }

  setAnswersByUfi(prev => {
    if (!(ufi in prev)) return prev;
    const next = { ...prev };
    delete next[ufi];
    // update storage immediately
    if (storageKey) safeSetItem(storageKey, next);
    return next;
  });
}, [resolveUfi, storageKey, fieldsMetadata, clearAttachFile]);

/**
 * resetAll: wipe all answers for current form.
 */
const resetAll = useCallback(() => {
  setAnswersByUfi({});
  if (storageKey) safeRemoveItem(storageKey);
  if (storageLastKey) safeRemoveItem(storageLastKey);

  // wipe otherText side-channel too
  setOtherTextByUfi({});
  if (otherStorageKey) safeRemoveItem(otherStorageKey);
  if (otherStorageLastKey) safeRemoveItem(otherStorageLastKey);

  clearAllAttachFiles();
  // eslint-disable-next-line no-console
//    console.log('[FormAnswersContext] All answers cleared for form', formId);
}, [storageKey, storageLastKey, otherStorageKey, otherStorageLastKey, formId, clearAllAttachFiles]);


  // -------------------------------------------
  // Submission helpers
  // -------------------------------------------

  /**
   * buildSubmissionArray: returns array of
   * { id, ufi, type, label, value, required }
   * ordered by current layout (rows order) and filtered to non-submit fields.
   * Value is **raw**:
   *  - single-select: optionId
   *  - multi-select: [optionId]
   *  - text/date: string
   *  - attach: object|null
   * That keeps your FormActionsContext’s dropdown → label and encode steps intact.
   */
  const buildSubmissionArray = useCallback(() => {
    if (!formId || !layoutConfig || !fieldsMetadata) return [];

    // Flatten current layout rows into field list
    const fieldsInLayout = Object.keys(layoutConfig).flatMap(rowKey => {
      return (layoutConfig[rowKey] || []).filter(f => f?.type !== 'submit');
    });

 const arr = fieldsInLayout.map(f => {
   const meta = fieldsMetadata?.[f.id] || {};
   const ufi  = meta?.ufi || f.id;
   const raw  = answersByUfi?.[ufi];
   const fallbackDefault =
     (meta.defaultValue !== undefined) ? meta.defaultValue : '';

   // Use canonical if present; otherwise fallback to defaultValue.
   let value = (raw !== undefined && raw !== null) ? raw : fallbackDefault;
   // Normalize attach: '' → null so required checks behave
   if (meta.type === 'attach' && value === '') value = null;

   // ─────────────────────────────────────────────────────────────
   // ENTERPRISE: attach token → File rehydrate (submit-time only)
   // - answersByUfi stores JSON-safe token: { kind:'file', tempId, ... }
   // - runtime cache holds the real File by tempId
   // - buildSubmissionArray must return a payload that includes the File
   //   so AUF uploads only happen on submit.
   // ─────────────────────────────────────────────────────────────
   if (meta.type === 'attach' && value && typeof value === 'object' && value.kind === 'file' && value.tempId) {
     const f = (typeof getAttachFile === 'function') ? getAttachFile(value.tempId) : null;

     // AttachFieldComponent / AUF flow should have registered this already
     if (f) {
       value = { ...value, file: f };
     }

     // Optional debug: confirm the file is present at submit-time
     if (typeof window !== 'undefined' && window.__DEBUG_AUF_FLICKER__ === true) {
       console.log('[AUF:Flicker][FormAnswersContext] buildSubmissionArray attach rehydrate', {
         formId,
         ufi,
         tempId: value.tempId,
         hasFile: !!f,
         fileName: f?.name || null,
         fileSize: f?.size || null,
       });
     }
   }

   return {
     id       : f.id,
     ufi,
     type     : meta.type || f.type,
     label    : meta.label || f.label || f.id,
     value,                                     // defaults now count as filled
     required : meta.required || f.required || false,
   };
 });


    // eslint-disable-next-line no-console
//    console.log('[FormAnswersContext] buildSubmissionArray →', arr);
    return arr;
}, [formId, layoutConfig, fieldsMetadata, answersByUfi, getAttachFile]);

  /**
   * buildDisplaySubmissionArray: like buildSubmissionArray but with
   * dropdowns mapped to labels and text transformed (caps/spacing).
   * You can use this for previews or live cards; this does *not* encode.
   */
  const buildDisplaySubmissionArray = useCallback(() => {
    const rawArr = buildSubmissionArray();
    return rawArr.map(item => {
      const meta = Object.values(fieldsMetadata || {}).find(m => m?.ufi === item.ufi) || fieldsMetadata?.[item.id] || {};
      if (isDropdownType(item.type)) {
        const setId = meta?.optionsSetUFI;
        const opts  = setId ? dropdownOptions?.[setId] || [] : [];

        if (item.type === 'single-select') {
          const m = opts.find(o => o.optionId === item.value);
          const lbl = m ? m.label : item.value;
          return { ...item, value: transformDisplay(lbl, meta) };
        } else {
          const arr = Array.isArray(item.value) ? item.value : [];
          const mapped = arr.map(id => {
            const m = opts.find(o => o.optionId === id);
            return transformDisplay(m ? m.label : id, meta);
          });
          return { ...item, value: mapped };
        }
      }
      // text/date/attach → transformed display
      return { ...item, value: transformDisplay(item.value, meta) };
    });
  }, [buildSubmissionArray, dropdownOptions, fieldsMetadata]);

  /**
   * buildChipValueMap
   * -----------------------------------------------------------------------------
   * Returns three maps for templating:
   *   - byUfi:     { [ufi]: rawValue }
   *   - byFieldId: { [fieldId]: rawValue }
   *   - merged:    { ...byUfi, ...byFieldId }  // fieldId wins on conflict (authoring bias)
   *
   * Value policy mirrors buildSubmissionArray:
   *   • Raw values (dropdowns are optionIds, not labels)
   *   • Defaults count when no canonical answer yet
   *   • Attach: '' normalized to null
   */
  const buildChipValueMap = useCallback(() => {
    const byUfi = {};
    const byFieldId = {};

    if (!formId || !layoutConfig || !fieldsMetadata) {
      return { byUfi, byFieldId, merged: {} };
    }

    const fieldsInLayout = Object.keys(layoutConfig).flatMap(rowKey =>
      (layoutConfig[rowKey] || []).filter(f => f?.type !== 'submit')
    );

    fieldsInLayout.forEach(f => {
      const meta = fieldsMetadata?.[f.id] || {};
      const ufi  = meta?.ufi || f.id;

      const raw = answersByUfi?.[ufi];
      const fallbackDefault = (meta.defaultValue !== undefined) ? meta.defaultValue : '';
      let value = (raw !== undefined && raw !== null) ? raw : fallbackDefault;
      if (meta.type === 'attach' && value === '') value = null;

      // Fill both maps
      byUfi[ufi] = value;
      byFieldId[f.id] = value;
    });

    // Authoring tools most often stamp data-field-id; prefer fieldId on collisions
    const merged = { ...byUfi, ...byFieldId };
    return { byUfi, byFieldId, merged };
  }, [formId, layoutConfig, fieldsMetadata, answersByUfi]);


// -------------------------------------------
// Diagnostics (optional)
// -------------------------------------------
useEffect(() => {
  if (typeof window === 'undefined') return;
  if (window.__DEBUG_AUF_FLICKER__ !== true) return;

  try {
    // find the first attach UFI (AUF typically has exactly one)
    const attachMeta = Object.values(fieldsMetadata || {}).find(m => m?.type === 'attach');
    const attachUfi = attachMeta?.ufi || null;
    if (!attachUfi) return;

    const v = answersByUfi?.[attachUfi];
    const hasFile = !!(v && typeof v === 'object' && v.file);

    console.log('[AUF:Flicker][FormAnswersContext] answersByUfi changed (attach view)', {
      formId,
      attachUfi,
      valueType: v == null ? 'null' : Array.isArray(v) ? 'array' : typeof v,
      keys: v && typeof v === 'object' ? Object.keys(v) : [],
      hasFile,
      fileName: hasFile ? (v.file?.name || null) : (v?.name || v?.filename || null),
      assetId: v?.assetId || v?.id || null,
      versionNumber: (typeof v?.versionNumber === 'number') ? v.versionNumber : null,
      previewUrl: v?.previewUrl || v?.previewURL || null,
    });
  } catch (e) {
    // non-fatal
  }
}, [answersByUfi, fieldsMetadata, formId]);

 // -------------------------------------------
 // Cleanup: cancel pending debounced saves on unmount / key change
 // -------------------------------------------
 useEffect(() => {
   return () => {
     if (saveTimerAnswers.current) {
       try { clearTimeout(saveTimerAnswers.current); } catch (_) {}
       saveTimerAnswers.current = null;
     }
     if (saveTimerOther.current) {
       try { clearTimeout(saveTimerOther.current); } catch (_) {}
       saveTimerOther.current = null;
     }
   };
 // Re-run cleanup whenever the target storage “slot” can change
 }, [storageKey, persist]);

// -------------------------------------------
// Provide context value
// -------------------------------------------
const ctxValue = useMemo(() => ({
  // core state
  answersByUfi,

  // getters
  getRawAnswer,
  getAnswer,

  getDisplayAnswer,
  getAnswersSnapshot,

  // other text channel
  getOtherText,
  setOtherText,
  clearOtherText,

  // attach runtime helpers (ephemeral; never persisted)
  // Exposed so AttachFieldComponent can:
  //  - register the file once (tempId -> File)
  //  - optionally read it back for preview
  registerAttachFile,
  getAttachFile,
  clearAttachFile,

  // setters
  setAnswer,
  clearAnswer,
  resetAll,

  // builders
  buildSubmissionArray,
  buildDisplaySubmissionArray,
  buildChipValueMap,
}), [
  answersByUfi,
  getRawAnswer,
  getDisplayAnswer,
  getAnswersSnapshot,
  getOtherText,
  setOtherText,
  clearOtherText,  
  registerAttachFile,
  getAttachFile,
  clearAttachFile,
  setAnswer,
  clearAnswer,
  resetAll,
  buildSubmissionArray,
  buildDisplaySubmissionArray,
  buildChipValueMap,
]);

return (
  <FormAnswersContext.Provider value={ctxValue}>
    {children}
  </FormAnswersContext.Provider>
);
};
